import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import { MenuSelect } from "components/Select/menuSelect";

import {
  channelOptions, // All channel options(default)
  countryDomainOptions, // For country domain related logic
  channelByCountryDomain, // For country domain related logic
  channelWidthOptions,
  wirelessModeOptions,
  txPowerOptions,
  rssiOptions,
} from "utils/channel-options/const";

import { ReactComponent as ToggleOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as ToggleOffIcon } from "assets/images/icons/Toggle-Off.svg";

const tabFields = [
  { key: "channel", label: "Channel" },
  { key: "chanWidth", label: "Channel Width" },
  { key: "wirelessMode", label: "Wireless Mode" },
  { key: "txPower", label: "Tx Power" },
  { key: "rssi", label: "RSSI Threshold" },
];

const bandMap = { 2: "2.4GHz", 5: "5GHz", 6: "6GHz" };
const tabKeys = ["2.4GHz", "5GHz", "6GHz"];

const getBandNumber = (bandKey) => {
  if (bandKey === "2.4GHz") return "2";
  if (bandKey === "5GHz") return "5";
  if (bandKey === "6GHz") return "6";
  return "";
};
//根據wifi6 or wifi7過濾各 band 的 wireless mode dropdown options
const getFilteredWirelessModeOptions = (options, radiosGenMap) => {
  const cloned = cloneDeep(options);

  if (cloned["2.4GHz"]) {
    const gen24 = radiosGenMap?.["2"];
    cloned["2.4GHz"] = cloned["2.4GHz"].filter((opt) => {
      if (gen24 === 6) return opt.wifi6 === true;
      if (gen24 === 7) return opt.wifi7 === true;
      return false;
    });
  }

  if (cloned["5GHz"]) {
    const gen5 = radiosGenMap?.["5"];
    cloned["5GHz"] = cloned["5GHz"].filter((opt) => {
      if (gen5 === 6) return opt.wifi6 === true;
      if (gen5 === 7) return opt.wifi7 === true;
      return false;
    });
  }

  if (cloned["6GHz"]) {
    const gen6 = radiosGenMap?.["6"];
    cloned["6GHz"] = cloned["6GHz"].filter((opt) => {
      if (gen6 === 6) return opt.wifi6 === true;
      if (gen6 === 7) return opt.wifi7 === true;
      return false;
    });
  }

  return cloned;
};

const RadioPanel = ({
  infoData,
  viewerPermissions,
  capabilityData,
  settingsGeneralData,
  wirelessRadioData,
  originalData,
  setOriginalData,
  setDataChangesFlag,
  handleDataChanged,
  dataCancelFlag,
  setDataCancelFlag,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [supportKeys, setSupportKeys] = useState([]);
  const [countryDomainIndex, setCountryDomainIndex] = useState(0);

  const [tabData, setTabData] = useState({
    "2.4GHz": { isOverrideOn: false, isHtCoexist: false, fieldValues: {} },
    "5GHz": { isOverrideOn: false, isDfsChannels: false, fieldValues: {} },
    "6GHz": { isOverrideOn: false, isPscChannels: false, fieldValues: {} },
  });

  const radiosGenMap = capabilityData?.radiosGen || {};

  const finalWirelessModeOptions = useMemo(() => {
    return getFilteredWirelessModeOptions(wirelessModeOptions, radiosGenMap);
  }, [radiosGenMap]);

  const currentKey = supportKeys[tabValue] || supportKeys[0] || "2.4GHz";
  const currentTab = tabData[currentKey] || {
    isOverrideOn: false,
    isHtCoexist: false,
    isDfsChannels: false,
    isPscChannels: false,
    fieldValues: {},
  };

  const handleTabChange = (e, newValue) => setTabValue(newValue);

  const getCountryDomainIndex = () => {
    const newValue = settingsGeneralData?.countryDomain?.countryIndex ?? 0;
    setCountryDomainIndex(newValue);
  }

  const getChannelOptions = (currentKey) => {
    if (countryDomainIndex) {
      const env = capabilityData?.env || "indoor";
      const countryDomain = countryDomainOptions[currentKey][countryDomainIndex.toString()]?.domain;

      if (!env || !countryDomain) {
        return channelOptions[currentKey];
      }

      const channels = channelByCountryDomain[currentKey][countryDomain][env] || [];
      const cloneOriginalChannels = cloneDeep(channelOptions);
      const newChannelOptions = [];

      cloneOriginalChannels[currentKey].forEach((option) => {
        if (channels.includes(+option.value) || option.value === "Auto") {
          newChannelOptions.push(option);
        }
      });

      return newChannelOptions;
    } else {
      return channelOptions[currentKey];
    }
  }

  const handleToggle = (toggleKey) => {
    const updatedTabData = cloneDeep(tabData);
    updatedTabData[currentKey][toggleKey] = !updatedTabData[currentKey][toggleKey];
    setTabData(updatedTabData);
    hasRadioChanged(updatedTabData);
  };

  const renderSpecialToggle = (key) => {
    const disabled = viewerPermissions || !currentTab.isOverrideOn;
    const textStyle = {
      ml: 1,
      fontSize: theme.typography.customFontSize[3],
      color: disabled
        ? theme.palette.text.disabled
        : theme.palette.text.primary,
    };

    if (currentKey === "2.4GHz" && key === "chanWidth") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={() => !disabled && handleToggle("isHtCoexist")}
        >
          {currentTab.isHtCoexist ? <ToggleOnIcon /> : <ToggleOffIcon />}
          <Typography sx={textStyle}>HT20/40 Coexistence</Typography>
        </Box>
      );
    }

    if (currentKey === "5GHz" && key === "channel" && currentTab.fieldValues.channel === "Auto") {
      // DAP-2810 does not support DFS channels, hide the toggle for it
      // from PP's suggestion.
      if (infoData?.modelName?.startsWith("DAP-2810")|| (infoData?.modelName?.startsWith("DAP-X2810"))) {
        return null;
      }

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={() => !disabled && handleToggle("isDfsChannels")}
        >
          {currentTab.isDfsChannels ? <ToggleOnIcon /> : <ToggleOffIcon />}
          <Typography sx={textStyle}>DFS Channels</Typography>
        </Box>
      );
    }

    /* if (currentKey === "6GHz" && key === "channel") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={() => !disabled && handleToggle("isPscChannels")}
        >
          {currentTab.isPscChannels ? <ToggleOnIcon /> : <ToggleOffIcon />}
          <Typography sx={textStyle}>
            Preferred Scanning Channel (PSC)
          </Typography>
        </Box>
      );
    } */

    return null;
  };

  const handleChange = (key, opt) => {
    const updatedTabData = cloneDeep(tabData);
    updatedTabData[currentKey].fieldValues[key] = opt.value;
    setTabData(updatedTabData);
    hasRadioChanged(updatedTabData);
  };

  const hasRadioChanged = (newTabData) => {
    if (!Array.isArray(wirelessRadioData)) return false;

    const currentData = tabKeys.map((key) => {
      const tab = newTabData[key];
      const bandNum = getBandNumber(key);

      return {
        override: !!tab.isOverrideOn,
        config: {
          band: bandNum,
          channel: tab.fieldValues.channel,
          chanWidth: tab.fieldValues.chanWidth,
          wirelessMode: tab.fieldValues.wirelessMode,
          txPower: tab.fieldValues.txPower,
          dfsChannels: !!tab.isDfsChannels,
          pscChannels: !!tab.isPscChannels,
          h2040Coexistence: !!tab.isHtCoexist,
          rssiThresholdEnable: tab.fieldValues.rssi !== "Disable",
          rssiThreshold:
            tab.fieldValues.rssi === "Disable"
              ? 1
              : tab.fieldValues.rssi,
        },
      };
    });

    const changed = JSON.stringify(currentData) !== JSON.stringify(wirelessRadioData);

    setDataChangesFlag((prev) => ({ ...prev, radio: changed }));
    if (changed) {
      const radioRequestData = tabKeys.map((key) => {
        const tab = newTabData[key];
        const bandNum = getBandNumber(key);
        const matchedConfig = Array.isArray(wirelessRadioData)
          ? wirelessRadioData.find((r) => r.config.band === bandNum)?.config
          : {};

        return {
          override: !!tab.isOverrideOn,
          config: {
            band: bandNum,
            id: matchedConfig?.id || "",
            channel: tab.fieldValues.channel,
            chanWidth: tab.fieldValues.chanWidth,
            wirelessMode: tab.fieldValues.wirelessMode,
            txPower: tab.fieldValues.txPower,
            dfsChannels: !!tab.isDfsChannels,
            pscChannels: !!tab.isPscChannels,
            h2040Coexistence: !!tab.isHtCoexist,
            rssiThresholdEnable: tab.fieldValues.rssi !== "Disable",
            rssiThreshold:
              tab.fieldValues.rssi === "Disable"
                ? 1
                : parseInt(tab.fieldValues.rssi),
          },
        };
      });

      handleDataChanged({ "wireless.radio": radioRequestData });
    }
  };

  useEffect(() => {
    if (!Array.isArray(wirelessRadioData)) return;

    const newData = {
      "2.4GHz": {
        isOverrideOn: false,
        isHtCoexist: false,
        isDfsChannels: false,
        isPscChannels: false,
        fieldValues: {},
      },
      "5GHz": {
        isOverrideOn: false,
        isHtCoexist: false,
        isDfsChannels: false,
        isPscChannels: false,
        fieldValues: {},
      },
      "6GHz": {
        isOverrideOn: false,
        isHtCoexist: false,
        isDfsChannels: false,
        isPscChannels: false,
        fieldValues: {},
      },
    };

    const updatedSupportKeys = [];
    wirelessRadioData.forEach((item) => {
      const { config = {}, override } = item;
      const key = bandMap[config.band];
      if (!key) return;

      newData[key] = {
        ...newData[key],
        isOverrideOn: !!override,
        isHtCoexist: !!config.h2040Coexistence,
        isDfsChannels: !!config.dfsChannels,
        isPscChannels: !!config.pscChannels,
        fieldValues: {
          channel: config.channel,
          chanWidth: config.chanWidth,
          wirelessMode: config.wirelessMode,
          txPower: config.txPower,
          rssi: config.rssiThresholdEnable
            ? config.rssiThreshold
            : "Disable",
        },
      };

      updatedSupportKeys.push(key);
    });
    setTabData(newData);
    setSupportKeys(updatedSupportKeys);

    getCountryDomainIndex();
  }, [wirelessRadioData, settingsGeneralData]);

  useEffect(() => {
    if (!supportKeys.length) return;
    if (tabValue > supportKeys.length - 1) {
      setTabValue(0);
    }
  }, [supportKeys, tabValue]);

  useEffect(() => {
    if (dataCancelFlag.radio === true && originalData["wireless.radio"]) {
      const radioOriginal = originalData["wireless.radio"];
      const newTabData = cloneDeep(tabData);

      radioOriginal.forEach((item) => {
        const { config = {}, override } = item;
        const key = bandMap[config.band];
        if (!key) return;

        newTabData[key] = {
          ...newTabData[key],
          isOverrideOn: !!override,
          isHtCoexist: !!config.h2040Coexistence,
          isDfsChannels: !!config.dfsChannels,
          isPscChannels: !!config.pscChannels,
          fieldValues: {
            channel: config.channel,
            chanWidth: config.chanWidth,
            wirelessMode: config.wirelessMode,
            txPower: config.txPower,
            rssi: config.rssiThresholdEnable
              ? config.rssiThreshold
              : "Disable",
          },
        };
      });

      setTabData(newTabData);
      setOriginalData((prev) => ({
        ...prev,
        "wireless.radio": radioOriginal,
      }));
      setDataCancelFlag((prev) => ({ ...prev, radio: false }));
    }
  }, [dataCancelFlag, originalData, tabData, setOriginalData, setDataCancelFlag]);

  const isDisabled = viewerPermissions || !currentTab.isOverrideOn;

  return (
    <CollapseCard title="Radio">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            border: `1px solid ${theme.palette.primary.greyThread}`,
            borderRadius: "100px",
            backgroundColor: theme.palette.primary.white,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              minHeight: 38,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 38,
                borderRadius: "100px",
                color: theme.palette.primary.black,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.subBlue,
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {supportKeys.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 4,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[1],
            }}
          >
            Override
          </Typography>
          <Box
            onClick={() => !viewerPermissions && handleToggle("isOverrideOn")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              width: 40,
              height: 22,
            }}
          >
            {currentTab.isOverrideOn ? <ToggleOnIcon /> : <ToggleOffIcon />}
          </Box>
        </Box>

        {tabFields.map(({ key, label }) => (
          <Box key={key}>
            <TwoColumnContainer label={label}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <MenuSelect
                  value={currentTab.fieldValues[key] || ""}
                  width="100%"
                  onChange={(opt) => handleChange(key, opt)}
                  options={
                    key === "channel"
                      ? getChannelOptions(currentKey) || []
                      : key === "chanWidth"
                        ? channelWidthOptions[currentKey] || []
                        : key === "wirelessMode"
                          ? finalWirelessModeOptions[currentKey] || []
                          : key === "txPower"
                            ? txPowerOptions
                            : key === "rssi"
                              ? rssiOptions
                              : []
                  }
                  disabled={isDisabled}
                />

                {renderSpecialToggle(key)}
              </Box>
            </TwoColumnContainer>
          </Box>
        ))}
      </Box>
    </CollapseCard>
  );
};

export default RadioPanel;
